/**
 * MyLeavePage — merged "My Leave Requests" + "My Leave Balances" in one place.
 * Tab 1: Applications (filterable, paginated, with export)
 * Tab 2: Balances (year picker, visual cards, export)
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Download, FilePlus2, FileText, ListChecks, Search, TrendingUp } from "lucide-react";

import { leaveApi } from "../api";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeavePagination } from "../components/LeavePagination";
import { LeaveStatusBadge } from "../components/LeaveStatusBadge";
import { useAsyncData, usePagination } from "../hooks";
import { exportToExcel, exportToPdf } from "../lib/exportUtils";
import { clearDraft, formatDraftAge, loadDraft } from "../lib/draft";
import { genderMatches } from "../lib/genderUtils";
import type { LeaveApplicationSummary, LeaveBalance, LeaveStatus } from "../types";
import { useEffect } from "react";

type Tab = "applications" | "balances";

const STATUS_TABS: { label: string; value: LeaveStatus | "ALL" }[] = [
  { label: "All", value: "ALL" },
  { label: "Pending", value: "PENDING_APPROVAL" },
  { label: "Approved", value: "APPROVED" },
  { label: "Drafts", value: "DRAFT" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
];

export default function MyLeavePage() {
  const [tab, setTab] = useState<Tab>("applications");

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="My Leave"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave" label="Dashboard" />
            <Link
              to="/hr/leave/requests/new"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
            >
              <FilePlus2 className="h-4 w-4" />
              Apply for Leave
            </Link>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="flex gap-1.5 rounded-2xl border bg-white p-1.5 shadow-sm">
        <TabBtn active={tab === "applications"} icon={TrendingUp} label="My Applications" onClick={() => setTab("applications")} />
        <TabBtn active={tab === "balances"} icon={ListChecks} label="My Balances" onClick={() => setTab("balances")} />
      </div>

      {tab === "applications" ? <ApplicationsTab /> : <BalancesTab />}
    </div>
  );
}

function TabBtn({ active, icon: Icon, label, onClick }: {
  active: boolean; icon: React.ComponentType<{ className?: string }>; label: string; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${active ? "bg-emerald-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ─── Applications tab ─────────────────────────────────────────────────────────

function ApplicationsTab() {
  const pagination = usePagination();
  const [status, setStatus] = useState<LeaveStatus | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const employee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const [draft, setDraft] = useState<ReturnType<typeof loadDraft>>(null);

  useEffect(() => {
    if (employee.data?.id) setDraft(loadDraft(employee.data.id));
  }, [employee.data?.id]);

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.myApplications(pagination.page, pagination.size),
    [pagination.page, pagination.size],
  );

  const filtered = useMemo<LeaveApplicationSummary[]>(() => {
    if (!data?.content) return [];
    return data.content.filter((a) => {
      const matchStatus = status === "ALL" || a.status === status;
      const q = search.trim().toLowerCase();
      return matchStatus && (!q || a.applicationNo.toLowerCase().includes(q) || a.leaveTypeName.toLowerCase().includes(q));
    });
  }, [data?.content, status, search]);

  const countByStatus = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of data?.content ?? []) m[a.status] = (m[a.status] ?? 0) + 1;
    return m;
  }, [data?.content]);

  function discardDraft() {
    if (employee.data?.id) clearDraft(employee.data.id);
    setDraft(null);
  }

  function fmtDate(v?: string | null) {
    if (!v) return "—";
    try { return new Date(`${v}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return v; }
  }

  return (
    <div className="space-y-4">
      {/* Draft resume banner */}
      {draft && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-bold text-amber-900">Unsaved draft</p>
              <p className="text-sm text-amber-700">Saved locally {formatDraftAge(draft.savedAt)}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/hr/leave/requests/new" className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700">Resume</Link>
            <button type="button" onClick={discardDraft} className="rounded-xl border border-amber-300 px-4 py-2 text-sm font-bold text-amber-700 hover:bg-amber-100">Discard</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map((t) => {
            const count = t.value === "ALL" ? (data?.totalElements ?? 0) : (countByStatus[t.value] ?? 0);
            const active = status === t.value;
            return (
              <button key={t.value} type="button" onClick={() => { setStatus(t.value); pagination.reset(); }}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-bold transition-colors ${active ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {t.label}
                {count > 0 && <span className={`rounded-full px-1.5 py-0.5 text-xs font-black ${active ? "bg-emerald-600" : "bg-slate-200 text-slate-600"}`}>{count}</span>}
              </button>
            );
          })}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="w-full rounded-xl border py-2 pl-9 pr-4 text-sm outline-none focus:border-emerald-500"
            placeholder="Search by application no. or leave type…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {loading && <LeaveLoading label="Loading your applications…" />}
      {error && <LeaveError message={error} onRetry={reload} />}

      {data && (
        <>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3.5">Application #</th>
                    <th className="px-4 py-3.5">Leave Type</th>
                    <th className="px-4 py-3.5">Start</th>
                    <th className="px-4 py-3.5">End</th>
                    <th className="px-4 py-3.5">Days</th>
                    <th className="px-4 py-3.5">Step</th>
                    <th className="px-4 py-3.5">Status</th>
                    <th className="px-4 py-3.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((a, i) => (
                    <tr key={a.id} className={`transition-colors hover:bg-emerald-50/40 ${i % 2 === 1 ? "bg-slate-50/50" : ""}`}>
                      <td className="whitespace-nowrap px-4 py-3.5 font-mono text-xs font-bold text-slate-700">{a.applicationNo}</td>
                      <td className="px-4 py-3.5 font-semibold text-slate-800">{a.leaveTypeName}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{fmtDate(a.startDate)}</td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-slate-600">{fmtDate(a.endDate)}</td>
                      <td className="px-4 py-3.5"><span className="inline-flex min-w-[2rem] justify-center rounded-lg bg-slate-100 px-2 py-0.5 font-black text-slate-700">{a.requestedDays}</span></td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{a.currentStepName || "—"}</td>
                      <td className="px-4 py-3.5"><LeaveStatusBadge value={a.status} /></td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-right">
                        <Link to={`/hr/leave/requests/${a.id}`} className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50">View →</Link>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-12 text-center">
                      <CalendarDays className="mx-auto h-8 w-8 text-slate-300" />
                      <p className="mt-2 text-sm text-slate-400">No applications found.</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <LeavePagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
        </>
      )}
    </div>
  );
}

// ─── Balances tab ─────────────────────────────────────────────────────────────

function BalancesTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [hideNonApplicable, setHideNonApplicable] = useState(true);

  const balances = useAsyncData(() => leaveApi.myBalances(year), [year]);
  const employee = useAsyncData(() => leaveApi.currentEmployee(), []);
  const allTypes = useAsyncData(() => leaveApi.activeLeaveTypes(), []);

  async function handlePdf() {
    if (!balances.data) return;
    setExporting(true);
    try {
      const { headers, rows } = buildExport(balances.data);
      await exportToPdf({ title: `My Leave Balances — ${year}`, subtitle: `${employee.data?.firstName ?? ""} ${employee.data?.lastName ?? ""}`, headers, rows, filename: `my-balances-${year}` });
    } finally { setExporting(false); }
  }

  async function handleExcel() {
    if (!balances.data) return;
    setExportingExcel(true);
    try {
      const { headers, rows } = buildExport(balances.data);
      await exportToExcel(`My Leave Balances — ${year}`, headers, rows, `my-balances-${year}`);
    } finally { setExportingExcel(false); }
  }

  // Build a map of leaveTypeId → applicableGender
  const typeGenderMap = useMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {};
    for (const t of allTypes.data ?? []) map[t.id] = t.applicableGender;
    return map;
  }, [allTypes.data]);

  const employeeGender = employee.data?.gender ?? null;

  const annotatedBalances = useMemo(() => (balances.data ?? []).map((b) => {
    const applicableGender = typeGenderMap[b.leaveTypeId] ?? "ALL";
    const isApplicable = genderMatches(employeeGender, applicableGender);
    return { b, applicableGender, isApplicable };
  }), [balances.data, typeGenderMap, employeeGender]);

  const visibleBalances = hideNonApplicable
    ? annotatedBalances.filter((r) => r.isApplicable)
    : annotatedBalances;

  const nonApplicableCount = annotatedBalances.filter((r) => !r.isApplicable).length;

  const totalPending = visibleBalances.reduce((s, { b }) => s + Number(b.pendingDays ?? 0), 0);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            Leave Year:
            <input type="number" className="w-24 rounded-xl border px-3 py-1.5 text-sm outline-none focus:border-emerald-500"
              value={year} min={2020} max={2100} onChange={(e) => setYear(Number(e.target.value))} />
          </label>
          {nonApplicableCount > 0 && (
            <button type="button" onClick={() => setHideNonApplicable((v) => !v)}
              className="rounded-xl border px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">
              {hideNonApplicable
                ? `Show all (${nonApplicableCount} not applicable)`
                : "Hide non-applicable"}
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={handlePdf} disabled={exporting || !balances.data}
            className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-40">
            <FileText className="h-4 w-4 text-red-500" /> PDF
          </button>
          <button type="button" onClick={handleExcel} disabled={exportingExcel || !balances.data}
            className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-40">
            <Download className="h-4 w-4 text-emerald-600" /> Excel
          </button>
        </div>
      </div>

      {balances.loading && <LeaveLoading label="Loading your balances…" />}
      {balances.error && <LeaveError message={balances.error} onRetry={balances.reload} />}

      {balances.data && visibleBalances.length > 0 && (
        <>
          {/* Summary row — only the pending-approval tile; a single "total remaining" figure
              summed across leave types (annual, sick, maternity, …) isn't a meaningful number,
              since those day allowances aren't interchangeable. Each type's own remaining
              balance is shown in its own card below instead. */}
          {totalPending > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4">
                <div className="text-xs font-bold uppercase tracking-wide text-amber-600">Days Pending Approval</div>
                <div className="mt-1 text-3xl font-black text-amber-700">{totalPending}</div>
              </div>
            </div>
          )}

          {/* Balance cards grid */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visibleBalances.map(({ b, applicableGender, isApplicable }) => {
              const pct = b.entitledDays > 0 ? Math.min(100, Math.round((b.remainingDays / b.entitledDays) * 100)) : 0;
              const tone = !isApplicable ? "slate" : pct <= 0 ? "red" : pct < 30 ? "amber" : "emerald";
              const bar = { red: "bg-red-400", amber: "bg-amber-400", emerald: "bg-emerald-500", slate: "bg-slate-300" }[tone];
              const remColor = { red: "text-red-600", amber: "text-amber-600", emerald: "text-emerald-700", slate: "text-slate-400" }[tone];
              return (
                <div key={b.id} className={`rounded-2xl border bg-white p-5 shadow-sm ${!isApplicable ? "opacity-60" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-black text-slate-900">{b.leaveTypeName}</div>
                    {applicableGender !== "ALL" && (
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${
                        applicableGender === "FEMALE" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"
                      }`}>
                        {applicableGender === "FEMALE" ? "♀ Female" : "♂ Male"}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">{b.leaveYear}</div>

                  <div className="mt-3 flex items-end justify-between">
                    <div>
                      <div className={`text-3xl font-black ${remColor}`}>{b.remainingDays}</div>
                      <div className="text-xs text-slate-400">of {b.entitledDays} days remaining</div>
                    </div>
                    <div className="text-right text-xs text-slate-500 space-y-0.5">
                      {b.broughtForwardDays > 0 && <div className="text-blue-600">+{b.broughtForwardDays} carried fwd</div>}
                      {b.pendingDays > 0 && <div className="text-amber-600">{b.pendingDays} pending</div>}
                      <div>{b.usedDays} used</div>
                    </div>
                  </div>

                  <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full transition-all ${bar}`} style={{ width: `${pct}%` }} />
                  </div>

                  {b.carryForwardExpiresOn && (
                    <div className="mt-2 text-xs text-amber-600">
                      ⚠ Carry-forward expires {b.carryForwardExpiresOn}
                    </div>
                  )}

                  {!isApplicable && (
                    <div className="mt-2 text-xs font-semibold text-slate-400">Not applicable for your gender</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {balances.data && visibleBalances.length === 0 && (
        <div className="rounded-2xl border bg-white p-10 text-center shadow-sm">
          <ListChecks className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-500">
            {balances.data.length === 0
              ? `No balance records for ${year}.`
              : "No applicable leave types for your gender. Toggle above to view all."}
          </p>
        </div>
      )}
    </div>
  );
}

function buildExport(balances: LeaveBalance[]) {
  const headers = ["Leave Type", "Year", "Entitled", "Brought Fwd", "Pending", "Used", "Forfeited", "Remaining"];
  const rows = balances.map((b) => [b.leaveTypeName, b.leaveYear, b.entitledDays, b.broughtForwardDays, b.pendingDays, b.usedDays, b.forfeitedDays, b.remainingDays]);
  return { headers, rows };
}
